import * as React from 'react';

import type { ValuePropItem } from '@coursera-web/cds-labs';

import {
  BriefcaseIcon,
  CalendarIcon,
  CashIcon,
  ClockOutlineIcon,
  GiftIcon,
  GlobeIcon,
  HelpIcon,
  LaptopIcon,
  MembersIcon,
} from '@coursera/cds-icons';

import type { AdmissionRequirementsModuleProps } from 'bundles/degree-description/components/modules/program-details/AdmissionRequirementsModule';
import type { CurriculumModuleProps } from 'bundles/degree-description/components/modules/program-details/CurriculumModule';
import type { PayingForYourDegreeModuleProps } from 'bundles/degree-description/components/modules/program-details/PayingForYourDegreeModule';

// Dummy content standing in for the program details tabs until they're wired up to a real source.

type TabValuePropsContent = {
  heading: string;
  description: string;
  valueProps: ValuePropItem[];
};

export const getWhatYoullLearnDummyContent = (): TabValuePropsContent => ({
  heading: "What you'll learn",
  description:
    "Are you ready to embark on a journey into the exciting world of degree learning? Whether you're a beginner or an experienced data enthusiast.",
  valueProps: [
    {
      id: 'business-decision-making',
      icon: <BriefcaseIcon size="large" />,
      headline: 'Business decision-making',
      description: 'Build strategic, financial, analytical, marketing, and leadership skills you can apply at work.',
    },
    {
      id: 'real-world-application',
      icon: <GlobeIcon size="large" />,
      headline: 'Real-world application',
      description: 'Practice business concepts through team projects, live sessions, and case-based learning.',
    },
    {
      id: 'managerial-confidence',
      icon: <MembersIcon size="large" />,
      headline: 'Managerial confidence',
      description: 'Strengthen communication, economics, accounting, innovation, and people leadership skills.',
    },
  ],
});

export const getAdmissionRequirementsDummyContent = (): AdmissionRequirementsModuleProps => ({
  heading: 'Admission requirements',
  description: 'Here are the basic details on getting into this degree program.',
  sections: [
    {
      sectionTitle: 'Minimum requirements',
      items: [
        {
          id: 'bachelors-degree',
          question: "Bachelor's degree or equivalent",
          answer: 'An accredited undergraduate degree in any field of study.',
        },
        {
          id: 'professional-experience',
          question: '3 years of professional working experience',
          answer: 'Full-time work experience in a relevant field is recommended but not required for all applicants.',
        },
      ],
    },
    {
      sectionTitle: 'Required application materials',
      items: [
        {
          id: 'academic-credentials',
          question: 'Academic credentials',
          answer: 'Official transcripts from all post-secondary institutions attended.',
        },
        {
          id: 'resume',
          question: 'Resume',
          answer: 'An up-to-date resume outlining your professional and academic experience.',
        },
        {
          id: 'personal-statement',
          question: 'Personal statement/essay',
          answer: 'A short statement describing your goals and motivation for pursuing this degree.',
        },
        {
          id: 'references',
          question: 'References',
          answer: 'Contact information for two or three professional or academic references.',
        },
        {
          id: 'english-proficiency',
          question: 'English proficiency scores',
          answer: 'Required for applicants whose first language is not English, unless a waiver applies.',
        },
      ],
    },
  ],
});

const curriculumCoursesPlaceholder = () => ['Course 1', 'Course 2', 'Course 3', 'Course 4'];

export const getCurriculumDummyContent = (): CurriculumModuleProps => ({
  heading: 'Curriculum',
  description: 'This is a list of the program details.',
  items: [
    {
      id: 'core-one',
      title: 'Core one: Strategic leadership & management',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'core-two',
      title: 'Core two: Managerial economics and business analysis',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'core-three',
      title: 'Core three: Value chain management',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'core-four',
      title: 'Core four: Financial management',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-global-challenges',
      title: 'Focus area: Global challenges in business',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-entrepreneurship',
      title: 'Focus area: Entrepreneurship and strategic innovation',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-digital-marketing',
      title: 'Focus area: Digital marketing',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-business-analytics',
      title: 'Focus area: Business analytics',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-mergers-acquisitions',
      title: 'Focus area: Mergers and acquisitions',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
    {
      id: 'focus-healthcare-innovation',
      title: 'Focus area: Healthcare innovation, design, and entrepreneurship',
      supportText: '4 courses',
      courses: curriculumCoursesPlaceholder(),
    },
  ],
});

export const getFormatAndScheduleDummyContent = (): TabValuePropsContent => ({
  heading: 'Format & schedule',
  description: "There's flexibility in how you want to learn through a structured timely format from the university.",
  valueProps: [
    {
      id: 'online',
      icon: <LaptopIcon size="large" />,
      headline: '100% online',
      description: 'Build strategic, financial, analytical, marketing, and leadership skills you can apply at work.',
    },
    {
      id: 'duration',
      icon: <CalendarIcon size="large" />,
      headline: '24-36 months',
      description: 'Practice business concepts through team projects, live sessions, and case-based learning.',
    },
    {
      id: 'weekly-hours',
      icon: <ClockOutlineIcon size="large" />,
      headline: '10-15 hours weekly',
      description: 'Strengthen communication, economics, accounting, innovation, and people leadership skills.',
    },
  ],
});

export const getPayingForYourDegreeDummyContent = (): PayingForYourDegreeModuleProps => ({
  heading: 'Paying for your degree',
  description: 'Here are the options to pay for your degree and requirements.',
  stats: [
    { id: 'credit-hours', headline: '72 credit hours required' },
    { id: 'cost-per-credit-hour', headline: '$379 cost per credit hour' },
    { id: 'total-tuition', headline: '$27,288 total tuition' },
  ],
  items: [
    {
      id: 'scholarships',
      icon: <GiftIcon size="large" />,
      headline: 'Scholarships and financial support',
      description: (
        <ul>
          <li>Competitive scholarships may be available for eligible learners</li>
        </ul>
      ),
    },
    {
      id: 'employer-assistance',
      icon: <BriefcaseIcon size="large" />,
      headline: 'Employer tuition assistance',
      description: (
        <ul>
          <li>Many employers fund degree and professional development programs</li>
        </ul>
      ),
    },
    {
      id: 'payment-options',
      icon: <CashIcon size="large" />,
      headline: 'Flexible payment options',
      description: (
        <ul>
          <li>Pay tuition as you take courses instead of all upfront</li>
        </ul>
      ),
    },
    {
      id: 'financial-resources',
      icon: <HelpIcon size="large" />,
      headline: 'Additional financial resources',
      description: (
        <ul>
          <li>Review funding paths that may fit your situation</li>
          <li>Learn about loans, grants, and tax benefits</li>
        </ul>
      ),
    },
  ],
});
